import React from "react";
import { Button } from "../ui/button";
import { PlayIcon } from "lucide-react";
import {
  TestRun,
  TestRunQualitativeEvaluationSummary,
} from "../../model_gen/evaluation";

interface Props {
  testRun: TestRun;
  testRunResults: TestRunQualitativeEvaluationSummary | null;
  onRunEvaluations: () => void;
}

const TestRunResultsSummary: React.FC<Props> = ({
  testRun,
  testRunResults,
  onRunEvaluations,
}) => {
  const scores = testRunResults?.request_scores || [];
  const totalScored = scores.reduce((s, i) => s + i.count, 0);
  const weightedSum = scores.reduce((s, i) => s + i.score * i.count, 0);
  const avgScore = totalScored > 0 ? weightedSum / totalScored : null;
  const exactMatchCount = testRunResults?.exact_match_count || 0;
  const completedCount = testRunResults?.completed_requests || 0;
  const accuracy =
    completedCount > 0 ? (exactMatchCount / completedCount) * 100 : null;

  return (
    <div className="mt-8 pt-6 border-t border-border">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-gc-med text-foreground">Results</h2>
        {testRun?.status === "COMPLETED" && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={onRunEvaluations}
          >
            <PlayIcon className="w-3.5 h-3.5" />
            Run Evaluations
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground font-gc-reg">
            Requests
          </div>
          <div className="text-xl font-gc-demi-bold text-foreground">
            {testRunResults?.total_requests?.toLocaleString() || 0}
          </div>
          <div className="text-xs text-muted-foreground font-gc-reg">
            {testRunResults ? (
              <>
                <span className="text-green-500">
                  {testRunResults.completed_requests.toLocaleString()} completed
                </span>
                {testRunResults.error_requests > 0 && (
                  <span className="text-red-500 ml-2">
                    {testRunResults.error_requests.toLocaleString()} errors
                  </span>
                )}
              </>
            ) : (
              "—"
            )}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground font-gc-reg">
            Accuracy
          </div>
          <div className="text-xl font-gc-demi-bold text-foreground">
            {accuracy !== null ? `${accuracy.toFixed(1)}%` : "—"}
          </div>
          <div className="text-xs text-muted-foreground font-gc-reg">
            {completedCount > 0
              ? `${exactMatchCount} / ${completedCount} exact match`
              : "—"}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground font-gc-reg">
            Avg Score
          </div>
          <div className="text-xl font-gc-demi-bold text-foreground">
            {avgScore !== null ? `${avgScore.toFixed(2)} / 5` : "—"}
          </div>
          <div className="text-xs text-muted-foreground font-gc-reg">
            {totalScored > 0 ? `${totalScored} evaluated` : "—"}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground font-gc-reg">
            Response Time
          </div>
          <div className="text-xl font-gc-demi-bold text-foreground">
            {testRunResults?.response_time_percentiles?.p50
              ? `${Math.round(testRunResults.response_time_percentiles.p50)}ms`
              : "—"}
          </div>
          <div className="text-xs text-muted-foreground font-gc-reg">
            {testRunResults?.response_time_percentiles?.p90
              ? `p90: ${Math.round(testRunResults.response_time_percentiles.p90)}ms`
              : "median (p50)"}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground font-gc-reg">
            Completion
          </div>
          <div className="text-xl font-gc-demi-bold text-foreground">
            {testRunResults && testRunResults.total_requests > 0
              ? `${Math.round((testRunResults.completed_requests / testRunResults.total_requests) * 100)}%`
              : "—"}
          </div>
          <div className="text-xs text-muted-foreground font-gc-reg">
            {testRunResults && testRunResults.error_requests > 0 ? (
              <span className="text-red-500">
                {testRunResults.error_requests} errors
              </span>
            ) : (
              "0 errors"
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestRunResultsSummary;
